import Swiper from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { Autoplay, Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';

/** Делим длину ленты на эту величину: при неполной «странице» дублируем первые карточки */
const RESIDENTS_SLIDES_PER_PAGE = 5;
const PADDED_SLIDE_CLASS = 'home-residents__slide--pad';

const BREAKPOINTS = {
  0: {
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 4,
  },
  576: {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 6,
  },
  768: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 8,
  },
  1200: {
    slidesPerView: 5,
    slidesPerGroup: 5,
    spaceBetween: 16,
  },
} satisfies SwiperOptions['breakpoints'];

export interface HomeResidentsSwiperWidgetOptions {
  rootSelector?: string;
  swiperOptions?: Partial<SwiperOptions>;
}

const defaultSwiperOptions = {
  modules: [Navigation, Autoplay],
  centeredSlides: false,
  slidesPerView: RESIDENTS_SLIDES_PER_PAGE,
  slidesPerGroup: RESIDENTS_SLIDES_PER_PAGE,
  spaceBetween: 20,
  autoplay: {
    delay: 4000,
    disableOnInteraction: false,
    pauseOnMouseEnter: false,
  },
  speed: 600,
  loopAddBlankSlides: false,
} satisfies Partial<SwiperOptions>;

export class HomeResidentsSwiperWidget {
  private readonly root: HTMLElement | null;
  private swiper: Swiper | null = null;
  private readonly extraOptions: Partial<SwiperOptions>;
  private cardHoverAbort: AbortController | null = null;
  private modalActive: boolean = false;

  constructor(options?: HomeResidentsSwiperWidgetOptions) {
    this.root = document.querySelector<HTMLElement>(options?.rootSelector ?? '.home-residents');
    this.extraOptions = options?.swiperOptions ?? {};
    this.modalActive = false;
  }

  public init(): void {
    if (!this.root) return;

    const el = this.root.querySelector<HTMLElement>('.swiper');
    if (!el) return;

    const wrapper = el.querySelector<HTMLElement>('.swiper-wrapper');
    if (!wrapper) return;

    this.destroy();
    this.removePaddedSlides();

    const slidesPerGroup = this.getSlidesPerGroup();

    this.padSlidesToFullPages(wrapper, slidesPerGroup);

    const slideCount = wrapper.children.length;

    const canLoop = slideCount >= slidesPerGroup * 2;
    if (slideCount === 0) return;

    this.swiper = new Swiper(el, {
      ...defaultSwiperOptions,
      ...this.extraOptions,

      modules: this.extraOptions.modules ?? defaultSwiperOptions.modules,

      breakpoints: BREAKPOINTS,

      slidesPerView: undefined,
      slidesPerGroup: undefined,

      centerInsufficientSlides: true,

      loop: canLoop,

      rewind: !canLoop,

      navigation: {
        nextEl: this.root.querySelector('.swiper-button-next'),
        prevEl: this.root.querySelector('.swiper-button-prev'),
        addIcons: false,
      },
    });

    this.bindCardHoverAutoplay(this.swiper, this.root);

    document.addEventListener('residentModal:autoplayStop', () => {
      this.modalActive = true;
      this.swiper?.autoplay?.stop();
    });

    document.addEventListener('residentModal:autoplayStart', () => {
      this.modalActive = false;
      this.swiper?.autoplay?.start();
    });
  }

  /** Пауза автопрокрутки при наведении на карточку резидента и при открытии модального окна. */
  private bindCardHoverAutoplay(swiper: Swiper, root: HTMLElement): void {
    this.cardHoverAbort?.abort();
    this.cardHoverAbort = new AbortController();
    const { signal } = this.cardHoverAbort;

    const onEnter = (): void => {
      if (this.modalActive) {
        return;
      }
      swiper.autoplay?.stop();
    };
    const onLeave = (): void => {
      if (this.modalActive) {
        return;
      }
      swiper.autoplay?.start();
    };

    root.querySelectorAll<HTMLElement>('.swiper-slide.resident-card').forEach((node) => {
      node.addEventListener('pointerenter', onEnter, { signal });
      node.addEventListener('pointerleave', onLeave, { signal });
    });
  }

  private getSlidesPerGroup(): number {
    const width = window.innerWidth;

    if (width >= 1200) return 5;
    if (width >= 768) return 3;
    if (width >= 576) return 2;

    return 1;
  }

  private removePaddedSlides(): void {
    if (!this.root) return;
    this.root.querySelectorAll<HTMLElement>(`.${PADDED_SLIDE_CLASS}`).forEach((node) => {
      node.remove();
    });
  }

  private padSlidesToFullPages(wrapper: HTMLElement, pageSize: number): void {
    const slides = Array.from(wrapper.querySelectorAll<HTMLElement>('.swiper-slide'));
    if (slides.length === 0) return;

    const remainder = slides.length % pageSize;
    if (remainder === 0) return;

    const toAdd = pageSize - remainder;
    for (let i = 0; i < toAdd; i += 1) {
      const source = slides[i % slides.length];
      if (!source) break;
      const clone = source.cloneNode(true) as HTMLElement;
      clone.classList.add(PADDED_SLIDE_CLASS);
      clone.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(clone);
    }
  }

  public destroy(): void {
    this.cardHoverAbort?.abort();
    this.cardHoverAbort = null;
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.removePaddedSlides();
  }
}
